import { PhoneNumberUtil, PhoneNumberFormat, PhoneNumberType } from "google-libphonenumber";
import { hasAccess, accessKeys } from "./userAccess";

const phoneUtil = PhoneNumberUtil.getInstance();

// allowInternational = true will allow phonenumbers to any country
export function validatePhoneNumber(phoneNumber, allowInternational = false) {
  const regionCode = getDefaultRegionCode();
  let parsedNumber;
  try {
    parsedNumber = phoneUtil.parse(phoneNumber, regionCode);
  } catch {
    return false;
  }

  return allowInternational
    ? phoneUtil.isValidNumber(parsedNumber)
    : phoneUtil.isValidNumberForRegion(parsedNumber, regionCode);
}

// Validates that the number is a valid number in the given country, e.g. "SE".
// Numbers without country code are parsed as numbers in that country.
export function validatePhoneNumberForRegion(phoneNumber, regionCode) {
  const parsedNumber = tryParse(phoneNumber, regionCode);
  return !!parsedNumber && phoneUtil.isValidNumberForRegion(parsedNumber, regionCode);
}

// Like validatePhoneNumberForRegion, but also requires a mobile number (i.e. one that can receive SMS)
export function validateMobilePhoneNumberForRegion(phoneNumber, regionCode) {
  if (!validatePhoneNumberForRegion(phoneNumber, regionCode)) {
    return false;
  }
  const numberType = phoneUtil.getNumberType(tryParse(phoneNumber, regionCode));
  return numberType === PhoneNumberType.MOBILE || numberType === PhoneNumberType.FIXED_LINE_OR_MOBILE;
}

function getDefaultRegionCode() {
  if (hasAccess(accessKeys.swedishCompany)) {
    return "SE";
  }

  if (hasAccess(accessKeys.englishCompany)) {
    return "GB";
  }

  if (hasAccess(accessKeys.frenchCompany)) {
    return "FR";
  }

  if (hasAccess(accessKeys.norwegianCompany)) {
    return "NO";
  }

  return "SE";
}

// All regions supported by libphonenumber, with localized country name and calling code.
// The default region is placed first, the rest are sorted by name.
export function getPhoneCountries(locale) {
  const defaultRegionCode = getDefaultRegionCode();
  let displayNames = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: "region" });
  } catch {
    // Fall back to showing only the region code
  }

  return phoneUtil
    .getSupportedRegions()
    .map((regionCode) => ({
      regionCode,
      callingCode: phoneUtil.getCountryCodeForRegion(regionCode),
      name: displayNames?.of(regionCode) ?? regionCode,
    }))
    .sort((a, b) =>
      a.regionCode === defaultRegionCode
        ? -1
        : b.regionCode === defaultRegionCode
          ? 1
          : a.name.localeCompare(b.name, locale),
    );
}

function tryParse(phoneNumber, regionCode) {
  if (!phoneNumber || !phoneNumber.trim()) {
    return null;
  }
  try {
    return phoneUtil.parse(phoneNumber, regionCode);
  } catch {
    return null;
  }
}

// Formats the number the way it's written after the country calling code,
// e.g. "70 813 46 80" for +46708134680 (without the national trunk prefix 0)
function formatWithoutCallingCode(parsedNumber) {
  const international = phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
  return international.replace(`+${parsedNumber.getCountryCode()}`, "").trim();
}

// getRegionCodeForNumber returns null for invalid numbers when the calling code is shared
// by several countries (e.g. +1), so fall back to the main country for the calling code
function getRegionCode(parsedNumber) {
  const regionCode =
    phoneUtil.getRegionCodeForNumber(parsedNumber) ??
    phoneUtil.getRegionCodeForCountryCode(parsedNumber.getCountryCode());
  // "ZZ" is unknown and "001" is non-geographic, neither can be selected
  return regionCode === "ZZ" || regionCode === "001" ? null : regionCode;
}

// Splits a stored phone number into region and a nationally formatted number for display.
// Numbers that can't be parsed are returned as is, with the default region.
export function splitPhoneNumber(phoneNumber, fallbackRegionCode = getDefaultRegionCode()) {
  const parsedNumber = tryParse(phoneNumber, fallbackRegionCode);
  if (!parsedNumber) {
    return { regionCode: fallbackRegionCode, nationalNumber: phoneNumber ?? "" };
  }

  const regionCode = getRegionCode(parsedNumber) ?? fallbackRegionCode;
  const nationalNumber = phoneUtil.isValidNumber(parsedNumber)
    ? formatWithoutCallingCode(parsedNumber)
    : (phoneNumber ?? "");
  return { regionCode, nationalNumber };
}

// Combines region and the number typed by the user into E.164 (e.g. +46701234567).
// If the number starts with + the region is taken from the number instead.
// Returns the raw input if it can't be parsed, so that validation will fail on it.
export function toE164PhoneNumber(nationalNumber, regionCode) {
  const parsedNumber = tryParse(nationalNumber, regionCode);
  if (!parsedNumber) {
    return nationalNumber?.trim() ? nationalNumber : null;
  }
  return phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
}

export function getRegionCodeForPhoneNumber(phoneNumber) {
  const parsedNumber = tryParse(phoneNumber, getDefaultRegionCode());
  return parsedNumber ? getRegionCode(parsedNumber) : null;
}

export function formatNationalPhoneNumber(nationalNumber, regionCode) {
  const parsedNumber = tryParse(nationalNumber, regionCode);
  // Only drop the calling code if the number belongs to the selected country,
  // otherwise e.g. +47... would be shown as if it was a number in the selected country
  return parsedNumber && phoneUtil.isValidNumberForRegion(parsedNumber, regionCode)
    ? formatWithoutCallingCode(parsedNumber)
    : nationalNumber;
}
